import React, { useState, useCallback, useMemo } from "react";
import {
  ContactFormData,
  ContactFormState,
  ContactFormProps,
  FormValidation,
} from "./types";
import {
  initialFormData,
  contactSubjects,
  validationPatterns,
  fieldConfigs,
} from "./data";
import { submitContactForm } from "./forms.actions";

const ContactForm: React.FC<ContactFormProps> = ({
  onSuccess,
  className = "",
  showPrivacyConsent = true,
}) => {
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [formState, setFormState] = useState<ContactFormState>({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    isValidating: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Memoized validation function for performance
  const validateField = useCallback(
    (name: string, value: string): string => {
      switch (name) {
        case "firstName":
        case "lastName":
          if (!value.trim()) {
            return `${
              name === "firstName" ? "First" : "Last"
            } name is required`;
          }
          if (!validationPatterns.name.test(value.trim())) {
            return `Please enter a valid ${
              name === "firstName" ? "first" : "last"
            } name`;
          }
          return "";

        case "email":
          if (!value.trim()) {
            return "Email address is required";
          }
          if (!validationPatterns.email.test(value.trim())) {
            return "Please enter a valid email address";
          }
          return "";

        case "phone":
          if (value && !validationPatterns.phone.test(value.trim())) {
            return "Please enter a valid phone number";
          }
          return "";

        case "subject":
          if (!value) {
            return "Please select a subject";
          }
          return "";

        case "message":
          if (!value.trim()) {
            return "Message is required";
          }
          if (value.trim().length < 10) {
            return "Message must be at least 10 characters";
          }
          if (value.trim().length > 2000) {
            return "Message must be less than 2000 characters";
          }
          return "";

        case "privacyConsent":
          if (showPrivacyConsent && !formData.privacyConsent) {
            return "You must agree to the Privacy Policy";
          }
          return "";

        default:
          return "";
      }
    },
    [formData.privacyConsent, showPrivacyConsent]
  );

  // Memoized form validation
  const formValidation = useMemo((): FormValidation => {
    const newErrors: Record<string, string> = {};

    Object.keys(formData).forEach((key) => {
      if (key === "privacyConsent" && !showPrivacyConsent) return;

      const error = validateField(
        key,
        formData[key as keyof ContactFormData] as string
      );
      if (error) {
        newErrors[key] = error;
      }
    });

    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors,
    };
  }, [formData, validateField, showPrivacyConsent]);

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;

      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));

      // Mark field as touched
      setTouched((prev) => ({ ...prev, [name]: true }));

      // Clear field error when user starts typing
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }

      // Clear general form error
      if (formState.isError) {
        setFormState((prev) => ({
          ...prev,
          isError: false,
          errorMessage: undefined,
        }));
      }
    },
    [errors, formState.isError]
  );

  const handleBlur = useCallback(
    (
      e: React.FocusEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      const error = validateField(name, value);

      if (error) {
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    },
    [validateField]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      // Reset form state
      setFormState({
        isSubmitting: false,
        isSuccess: false,
        isError: false,
        isValidating: true,
      });

      // Validate all fields
      const validation = formValidation;
      if (!validation.isValid) {
        setErrors(validation.errors);
        setFormState((prev) => ({
          ...prev,
          isValidating: false,
          isError: true,
          errorMessage: "Please correct the errors below and try again.",
        }));

        // Focus on first error field
        const firstErrorField = Object.keys(validation.errors)[0];
        const element = document.getElementById(
          fieldConfigs[firstErrorField as keyof typeof fieldConfigs]?.id ||
            firstErrorField
        );
        element?.focus();

        return;
      }

      setFormState((prev) => ({
        ...prev,
        isSubmitting: true,
        isValidating: false,
      }));

      try {
        const response = await submitContactForm(formData);

        if (response.success) {
          setFormState({
            isSubmitting: false,
            isSuccess: true,
            isError: false,
            formId: response.formId,
          });

          // Reset form data on success
          setFormData(initialFormData);
          setErrors({});
          setTouched({});

          // Call success callback if provided
          if (onSuccess) {
            onSuccess(response);
          }

          // Focus on success message for screen readers
          setTimeout(() => {
            const successElement = document.getElementById(
              "contact-form-success"
            );
            successElement?.focus();
          }, 100);
        } else {
          setFormState({
            isSubmitting: false,
            isSuccess: false,
            isError: true,
            errorMessage: response.message,
          });

          // Set field-specific errors if provided
          if (response.errors) {
            setErrors(response.errors);
          }
        }
      } catch (error) {
        setFormState({
          isSubmitting: false,
          isSuccess: false,
          isError: true,
          errorMessage: "An unexpected error occurred. Please try again later.",
        });
      }
    },
    [formData, formValidation, onSuccess]
  );

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 ${className}`}
      noValidate
      role="form"
      aria-labelledby="contact-form-heading"
    >
      {/* Success Message */}
      {formState.isSuccess && (
        <div
          id="contact-form-success"
          className="p-4 mb-6 text-sm border-l-4 border-green-500 bg-green-50 text-green-700"
          role="alert"
          aria-live="polite"
          tabIndex={-1}
        >
          <div className="font-medium">Message Sent Successfully!</div>
          <p>
            Thank you for contacting us. We'll get back to you within 24 hours.
          </p>
          {formState.formId && (
            <p className="mt-2 text-xs">Reference ID: {formState.formId}</p>
          )}
        </div>
      )}

      {/* Error Message */}
      {formState.isError && (
        <div
          className="p-4 mb-6 text-sm border-l-4 border-red-500 bg-red-50 text-red-700"
          role="alert"
          aria-live="polite"
        >
          <div className="font-medium">
            There was a problem sending your message
          </div>
          <p>{formState.errorMessage || "Please try again later."}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Name */}
        <div>
          <label
            htmlFor={fieldConfigs.firstName.id}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {fieldConfigs.firstName.label} *
          </label>
          <input
            {...fieldConfigs.firstName}
            value={formData.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
              errors.firstName ? "border-red-500" : "border-gray-300"
            }`}
            disabled={formState.isSubmitting || formState.isSuccess}
            aria-invalid={!!errors.firstName}
            aria-describedby={
              errors.firstName
                ? `${fieldConfigs.firstName.id}-error`
                : undefined
            }
          />
          {errors.firstName && (
            <p
              id={`${fieldConfigs.firstName.id}-error`}
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.firstName}
            </p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label
            htmlFor={fieldConfigs.lastName.id}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {fieldConfigs.lastName.label} *
          </label>
          <input
            {...fieldConfigs.lastName}
            value={formData.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
              errors.lastName ? "border-red-500" : "border-gray-300"
            }`}
            disabled={formState.isSubmitting || formState.isSuccess}
            aria-invalid={!!errors.lastName}
            aria-describedby={
              errors.lastName ? `${fieldConfigs.lastName.id}-error` : undefined
            }
          />
          {errors.lastName && (
            <p
              id={`${fieldConfigs.lastName.id}-error`}
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.lastName}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email */}
        <div>
          <label
            htmlFor={fieldConfigs.email.id}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {fieldConfigs.email.label} *
          </label>
          <input
            {...fieldConfigs.email}
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
              errors.email ? "border-red-500" : "border-gray-300"
            }`}
            disabled={formState.isSubmitting || formState.isSuccess}
            aria-invalid={!!errors.email}
            aria-describedby={
              errors.email ? `${fieldConfigs.email.id}-error` : undefined
            }
          />
          {errors.email && (
            <p
              id={`${fieldConfigs.email.id}-error`}
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.email}
            </p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label
            htmlFor={fieldConfigs.phone.id}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {fieldConfigs.phone.label} (Optional)
          </label>
          <input
            {...fieldConfigs.phone}
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
              errors.phone ? "border-red-500" : "border-gray-300"
            }`}
            disabled={formState.isSubmitting || formState.isSuccess}
            aria-invalid={!!errors.phone}
            aria-describedby={
              errors.phone ? `${fieldConfigs.phone.id}-error` : undefined
            }
          />
          {errors.phone && (
            <p
              id={`${fieldConfigs.phone.id}-error`}
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.phone}
            </p>
          )}
        </div>
      </div>

      {/* Subject */}
      <div>
        <label
          htmlFor={fieldConfigs.subject.id}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {fieldConfigs.subject.label} *
        </label>
        <select
          id={fieldConfigs.subject.id}
          name={fieldConfigs.subject.name}
          value={formData.subject}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
            errors.subject ? "border-red-500" : "border-gray-300"
          }`}
          disabled={formState.isSubmitting || formState.isSuccess}
          aria-invalid={!!errors.subject}
          aria-describedby={
            errors.subject ? `${fieldConfigs.subject.id}-error` : undefined
          }
        >
          <option value="">Select a subject</option>
          {contactSubjects.map((subject) => (
            <option key={subject.value} value={subject.value}>
              {subject.label}
            </option>
          ))}
        </select>
        {errors.subject && (
          <p
            id={`${fieldConfigs.subject.id}-error`}
            className="mt-1 text-sm text-red-600"
            role="alert"
          >
            {errors.subject}
          </p>
        )}
      </div>

      {/* Message */}
      <div>
        <label
          htmlFor={fieldConfigs.message.id}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {fieldConfigs.message.label} *
          <span className="text-xs text-gray-500 ml-2">
            ({formData.message.length}/{fieldConfigs.message.maxLength}{" "}
            characters)
          </span>
        </label>
        <textarea
          id={fieldConfigs.message.id}
          name={fieldConfigs.message.name}
          value={formData.message}
          onChange={handleChange}
          onBlur={handleBlur}
          rows={5}
          maxLength={fieldConfigs.message.maxLength}
          placeholder={fieldConfigs.message.placeholder}
          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors resize-vertical ${
            errors.message ? "border-red-500" : "border-gray-300"
          }`}
          disabled={formState.isSubmitting || formState.isSuccess}
          aria-invalid={!!errors.message}
          aria-describedby={
            errors.message ? `${fieldConfigs.message.id}-error` : undefined
          }
        />
        {errors.message && (
          <p
            id={`${fieldConfigs.message.id}-error`}
            className="mt-1 text-sm text-red-600"
            role="alert"
          >
            {errors.message}
          </p>
        )}
      </div>

      {/* Privacy Policy Agreement */}
      {showPrivacyConsent && (
        <div>
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="contact-privacy"
                name="privacyConsent"
                type="checkbox"
                checked={formData.privacyConsent}
                onChange={handleChange}
                className={`w-4 h-4 border rounded bg-gray-50 focus:ring-3 focus:ring-cannabis-green-300 ${
                  errors.privacyConsent ? "border-red-500" : "border-gray-300"
                }`}
                disabled={formState.isSubmitting || formState.isSuccess}
                aria-invalid={!!errors.privacyConsent}
                aria-describedby={
                  errors.privacyConsent ? "privacy-error" : undefined
                }
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="contact-privacy" className="text-gray-700">
                I agree to the{" "}
                <a
                  href="/privacy-policy"
                  className="text-cannabis-green-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{" "}
                and consent to having my data processed for the purpose of
                responding to my inquiry. *
              </label>
            </div>
          </div>
          {errors.privacyConsent && (
            <p
              id="privacy-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.privacyConsent}
            </p>
          )}
        </div>
      )}

      {/* Submit Button */}
      <div>
        <button
          type="submit"
          className="w-full px-8 py-3 bg-cannabis-green-500 hover:bg-cannabis-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition duration-300 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-cannabis-green-300 focus:ring-offset-2"
          disabled={
            formState.isSubmitting ||
            formState.isSuccess ||
            formState.isValidating ||
            !formValidation.isValid
          }
          aria-describedby="submit-button-status"
        >
          {formState.isSubmitting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Sending Message...
            </>
          ) : formState.isSuccess ? (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Message Sent Successfully!
            </>
          ) : (
            "Send Message"
          )}
        </button>
        <p id="submit-button-status" className="sr-only" aria-live="polite">
          {formState.isSubmitting
            ? "Submitting your message..."
            : formState.isSuccess
            ? "Message sent successfully"
            : formState.isError
            ? "Error sending message"
            : "Ready to send message"}
        </p>
      </div>
    </form>
  );
};

export default ContactForm;
